// eslint-disable-next-line no-unused-vars
import axiosCore, { AxiosPromise, AxiosResponse } from 'axios'
import {
  IMAGES_SOURCES,
  SOURCE_FILE_TYPES,
  TEXTS, TRANSITIONS,
  FILE_TYPE,
  MEDIA_FILE_FILTERS,
  FILE_FETCH_URL_PATH,
  SOURCE_FILE_PROPERTIES
} from 'enums'
import { getLang } from 'helpers/getLang'
import https from 'https'
import { v4 as uuidv4 } from 'uuid'
import { translations } from 'translations/translations'
import Url from 'url'

import { handleError } from '~/errors/handleError'
import { getObjectFields } from '~/Util'
import { getApiUrlPathByType, getFileTypeById } from '~/helpers/assets/fetchFile'

const isHTTPS = (__API_HTTPS__ && __API_HTTPS__ === 'true') || window.location.protocol === 'https:'
const getPortBasedOnProtocol = () => isHTTPS ? 443 : 80
const getPortFromUrl = () => ((window.location.port !== '') && (__APP_PROFILE__ !== 'package') ? window.location.port : getPortBasedOnProtocol())
const baseHost = __BACKEND_HOST__ || window.location.hostname
const backendHost = __APP_PROFILE__ === 'package' ? (localStorage.getItem('solveig-editor-back-url') || baseHost)
  .replace('https://', '').replace('http://', '').replace('/', '') : baseHost
const apiPort = __API_PORT__ || getPortFromUrl()
const wsPort = __WS_PORT__ || getPortFromUrl()

const HOST_API = Url.format({
  protocol: isHTTPS ? 'https' : 'http',
  hostname: backendHost,
  port: apiPort,
})
const HOST_WS = Url.format({
  protocol: isHTTPS ? 'wss' : 'ws',
  hostname: backendHost,
  port: wsPort,
  pathname: '/websocket',
})

const axios = axiosCore.create()

axios.defaults.baseURL = HOST_API
axios.defaults.headers.post['Content-Type'] = 'application/json;charset=utf-8'


const getClientId = () => {
  // this id is different in another tab persist the same on reload page in the same tab.
  // used to control edit project from different tabs
  let clientId = sessionStorage.getItem('clientId')
  if (!clientId) {
    clientId = uuidv4()
    sessionStorage.setItem('clientId', clientId)
  }
  return clientId
}
/**
 * @namespace API
 */

/**
 * @typedef {object} API.BatchResponse
 * @property {string} id
 */

/**
 * @typedef {object} API.BatchRequestStatus
 * @property {string} id
 * @property {number} progress
 * @property {string} status
 * @property {object} error
 * @property {string[]} fileIds
 */

/**
 * @typedef {object} API.AxiosResponse
 * @template T
 * @property {number} status
 * @property {string} statusText
 * @property {T} data
 */

/**
 * @typedef {object} API.FilesResourceResponse
 * @template T
 * @property {number} total_count
 * @property {T[]} entries
 */

// TODO: describe shapes for each files type

export function setAuthToken(authToken) {
  axios.defaults.headers.common.Authorization = authToken
}

export function getVersion() {
  return axios.get('/api/version', { crossDomain: true })
}

export function fetchTemporaryToken(data, abortController) {
  return axios.post('/api/tokens', { ...data, signal: abortController?.signal })
}

export function deleteTemporaryToken(token, abortController) {
  return axios.delete(`/api/tokens/${token}`, {
    signal: abortController?.signal,
  })
}

export function checkToken(authToken) {
  return axios.get(`/api/tokens/${authToken}`, { crossDomain: true })
}

async function getPermanentUserFolders() {
  const params = {
    start_number: 0,
    count: __CFG__.SOURCE_FILES_MANAGEMENT.LOADING_NUMBER,
    filter: MEDIA_FILE_FILTERS.Folder,
  }
  const config = {
    params,
    crossDomain: true,
  }
  const resp = await axios.get('/api/mediafiles', config)
  return resp.data.entries
}

function getBackendMedia(params) {
  const config = {
    params,
    crossDomain: true,
  }
  return axios.get('/api/mediafiles', config)
}

/**
 * @param {object} [options]
 * TODO: options list
 * @returns {Promise<AxiosResponse<FilesResourceResponse<Object>>}
 */
export async function getMedia(options = {}) {
  const {
    start = 0,
    count = __CFG__.SOURCE_FILES_MANAGEMENT.LOADING_NUMBER,
    filter = undefined,
    order,
    sort,
    search = undefined,
    folder = undefined,
  } = options

  const optionalParams = { filter, search }

  const params = {
    start_number: start,
    count,
    folder,
    sort_order: order,
    sort_field: sort,
    ...Object.keys(optionalParams).filter(k => !!optionalParams[k])
      .reduce((acc, k) => ({ ...acc, [k]: optionalParams[k] }), {}),
  }
  let resp
  const { customApiUrls, customApiAuthToken, permanentFoldersIds } = axios.defaults
  if (customApiUrls?.mediaTabContentUrl) {
    if (folder && permanentFoldersIds?.filter(id => id === folder)) {
      // permanent folder content requested
      resp = await getBackendMedia(params)
    } else {
      const config = {
        params,
        headers: {
          Authorization: customApiAuthToken,
        },
        crossDomain: true,
      }
      resp = await axios.get(customApiUrls.mediaTabContentUrl, config)
      const { data } = resp
      data.entries = data.entries.map(entry => ({
        ...entry,
        type: FILE_TYPE.DEVMEDIA,
      }))
      if (customApiUrls?.mediaTabContentUrl && start === 0 && !folder) {
        // fetch default folders for user into which import, voiceover and output will be placed
        const permanentUserFolders = await getPermanentUserFolders()
        if (permanentUserFolders.length) {
          data.entries.unshift(...permanentUserFolders)
          data.total_count += permanentUserFolders.length
        }
      }
    }
  } else {
    resp = await getBackendMedia(params)
  }
  return resp
}

// TODO: not implemented
/**
 * @returns {Promise<API.AxiosResponse<API.FilesResourceResponse<object>>>}
 */
export function fetchTextFiles(textFileId) {
  const entries = [
    {
      text: SOURCE_FILE_PROPERTIES.TEXTS.DEFAULT.NAME,
      id: SOURCE_FILE_PROPERTIES.TEXTS.DEFAULT.ID,
      type: TEXTS.DEFAULT_TEXT,
      description: 'Text',
    },
  ]
  if (textFileId) {
    const data = entries.find(({ id }) => id === textFileId)
    if (data) {
      return {
        data,
      }
    }
  }
  return {
    data: {
      entries,
      total_count: entries.length,
    },
  }
}

// TODO: not implemented
/**
 * @returns {Promise<API.AxiosResponse<API.FilesResourceResponse<object>>>}
 */
export function fetchTransition(transitionId) {
  const transitionTemplatesBaseUrl = `${HOST_API}/transition_templates`
  const entries = [
    {
      name: SOURCE_FILE_PROPERTIES.TRANSITIONS.DISSOLVE.NAME,
      type: TRANSITIONS.DISSOLVE,
      sourceFileType: SOURCE_FILE_TYPES.TRANSITIONS,
      id: SOURCE_FILE_PROPERTIES.TRANSITIONS.DISSOLVE.ID,
      previewUrl: `${transitionTemplatesBaseUrl}/dissolve.mp4`,
    },
    {
      name: SOURCE_FILE_PROPERTIES.TRANSITIONS.FADE_IN.NAME,
      type: TRANSITIONS.FADEIN,
      sourceFileType: SOURCE_FILE_TYPES.TRANSITIONS,
      id: SOURCE_FILE_PROPERTIES.TRANSITIONS.FADE_IN.ID,
      previewUrl: `${transitionTemplatesBaseUrl}/fade_in.mp4`,
    },
    {
      name: SOURCE_FILE_PROPERTIES.TRANSITIONS.FADE_OUT.NAME,
      type: TRANSITIONS.FADEOUT,
      sourceFileType: SOURCE_FILE_TYPES.TRANSITIONS,
      id: SOURCE_FILE_PROPERTIES.TRANSITIONS.FADE_OUT.ID,
      previewUrl: `${transitionTemplatesBaseUrl}/fade_out.mp4`,
    },
    {
      name: SOURCE_FILE_PROPERTIES.TRANSITIONS.DIP_TO_BLACK.NAME,
      type: TRANSITIONS.DIP_BLACK,
      sourceFileType: SOURCE_FILE_TYPES.TRANSITIONS,
      id: SOURCE_FILE_PROPERTIES.TRANSITIONS.DIP_TO_BLACK.ID,
      previewUrl: `${transitionTemplatesBaseUrl}/dip_black.mp4`,
    },
    {
      name: SOURCE_FILE_PROPERTIES.TRANSITIONS.DIP_TO_WHITE.NAME,
      type: TRANSITIONS.DIP_WHITE,
      sourceFileType: SOURCE_FILE_TYPES.TRANSITIONS,
      id: SOURCE_FILE_PROPERTIES.TRANSITIONS.DIP_TO_WHITE.ID,
      previewUrl: `${transitionTemplatesBaseUrl}/dip_white.mp4`,
    },
    {
      name: SOURCE_FILE_PROPERTIES.TRANSITIONS.BLUR.NAME,
      type: TRANSITIONS.BLUR,
      sourceFileType: SOURCE_FILE_TYPES.TRANSITIONS,
      id: SOURCE_FILE_PROPERTIES.TRANSITIONS.BLUR.ID,
      previewUrl: `${transitionTemplatesBaseUrl}/blur.mp4`,
    },
    {
      name: SOURCE_FILE_PROPERTIES.TRANSITIONS.WIPE_RIGHT.NAME,
      type: TRANSITIONS.WIPE_RIGHT,
      sourceFileType: SOURCE_FILE_TYPES.TRANSITIONS,
      id: SOURCE_FILE_PROPERTIES.TRANSITIONS.WIPE_RIGHT.ID,
      previewUrl: `${transitionTemplatesBaseUrl}/wipe_right.mp4`,
    },
    {
      name: SOURCE_FILE_PROPERTIES.TRANSITIONS.WIPE_LEFT.NAME,
      type: TRANSITIONS.WIPE_LEFT,
      sourceFileType: SOURCE_FILE_TYPES.TRANSITIONS,
      id: SOURCE_FILE_PROPERTIES.TRANSITIONS.WIPE_LEFT.ID,
      previewUrl: `${transitionTemplatesBaseUrl}/wipe_left.mp4`,
    },
    {
      name: SOURCE_FILE_PROPERTIES.TRANSITIONS.WIPE_UP.NAME,
      type: TRANSITIONS.WIPE_UP,
      sourceFileType: SOURCE_FILE_TYPES.TRANSITIONS,
      id: SOURCE_FILE_PROPERTIES.TRANSITIONS.WIPE_UP.ID,
      previewUrl: `${transitionTemplatesBaseUrl}/wipe_up.mp4`,
    },
    {
      name: SOURCE_FILE_PROPERTIES.TRANSITIONS.WIPE_DOWN.NAME,
      type: TRANSITIONS.WIPE_DOWN,
      sourceFileType: SOURCE_FILE_TYPES.TRANSITIONS,
      id: SOURCE_FILE_PROPERTIES.TRANSITIONS.WIPE_DOWN.ID,
      previewUrl: `${transitionTemplatesBaseUrl}/wipe_down.mp4`,
    },
  ]
  if (transitionId) {
    const transition = entries.find(({ id, type }) => [ id, type ].includes(transitionId))
    if (transition) {
      return {
        data: transition,
      }
    }
  }
  return {
    data: {
      entries,
      total_count: entries.length,
    },
  }
}

export async function getMediaFile(id, fileType, sourceFileType,
  throwTypedError = true, controller = new AbortController()) {
  let apiUrl
  let type

  if (fileType) {
    type = fileType
  } else {
    const { fileType: prefix } = getFileTypeById(id)
    type = prefix
  }
  const config = {
    crossDomain: true,
    signal: controller.signal,
    headers: {
      Authorization: axios.defaults.headers.common.Authorization,
    },
  }

  switch (type) {
    case FILE_TYPE.DEVMEDIA:
    {
      const { customApiUrls, customApiAuthToken } = axios.defaults
      apiUrl = customApiUrls.fetchFileUrl
      config.headers.Authorization = customApiAuthToken
      break
    }
    default: {
      apiUrl = getApiUrlPathByType(type) || FILE_FETCH_URL_PATH[FILE_TYPE.UPLOADED]
    }
  }

  const imageTypes = [
    FILE_TYPE.IMAGE_LIB_PEXELS,
    FILE_TYPE.IMAGE_LIB_UNSPLASH,
    FILE_TYPE.IMAGE_LIB,
  ]

  if (sourceFileType === SOURCE_FILE_TYPES.IMAGES || imageTypes.includes(type)) {
    apiUrl = getApiUrlPathByType(FILE_TYPE.IMAGE_LIB)
    if (id.includes(IMAGES_SOURCES.Pexels)) {
      apiUrl += `/${IMAGES_SOURCES.Pexels}`
    } else if (id.includes(IMAGES_SOURCES.Unsplash)) {
      apiUrl += `/${IMAGES_SOURCES.Unsplash}`
    } else {
      apiUrl += `/${IMAGES_SOURCES.Native}`
    }
  }

  const resp = await axios.get(`${apiUrl}/${id}`, config)
  if (throwTypedError) {
    handleError(resp)
  }
  if (type === FILE_TYPE.DEVMEDIA && resp.data) {
    resp.data.type = FILE_TYPE.DEVMEDIA
  }
  return resp
}

/**
 *
 * @param {*} id
 * @returns {AxiosPromise<{ progress:  number, status: string, error: any }>}
 */
export function getMediaFileStatus(id, abortController = new AbortController()) {
  return axios.get(`/api/mediafile/status?id=${id}&uid=${Date.now()}`,
    { crossDomain: true, signal: abortController.signal })
}

export function getThumbnail(fname = '', width = 174, height = 113, time = 0) {
  return axios.get(`/api/testfiles/thumbnails/init?fname=${fname}&width=${width}&height=${height}&time=${time}`, { crossDomain: true })
}

export function getCachedThumbnails(fileId, count = 1, startTime = 0, step = 0) {
  return axios.get(`/api/clip_thumbnails?id=${fileId}&start_time=${startTime}&step=${step}&count=${count}&cached=true`, { crossDomain: true })
}

export function getCachedThumbnail({ fileId, type, time = 0, abortController }) {
  let apiUrl
  const config = {
    crossDomain: true,
    signal: abortController.signal,
    headers: {
      Authorization: axios.defaults.headers.common.Authorization,
    },
  }
  switch (type) {
    case FILE_TYPE.DEVMEDIA:
    {
      const { customApiUrls, customApiAuthToken } = axios.defaults
      apiUrl = customApiUrls.fetchThumbnailUrl
      config.headers.Authorization = customApiAuthToken
      break
    }
    default: {
      apiUrl = '/api/clip_thumbnail'
    }
  }
  return axios.get(`${apiUrl}?id=${fileId}&time=${time}&cached=true`, config)
}

export function createEditingTask(xtl, activeProjectId) {
  return axios.post('/api/task/editing', {
    xtl_task: xtl,
    projectId: activeProjectId,
  })
}

export function uploadOverlayImage(imageBlob, abortController) {
  const config = {
    headers: {
      'Content-Type': 'application/octet-stream',
    },
    signal: abortController.signal,
  }
  return axios.post('/api/overlay_img', imageBlob, config)
}

export async function getEditingTaskStatus(taskId) {
  const { data } = await axios.get(`/api/task/editing?id=${taskId}`)
  return data
}

export function deleteEditingTask(taskId) {
  return axios.delete('/api/task/editing', {
    params: {
      id: taskId,
    },
  })
}

export function createHldTask({ fileId, onnxName, targetDurationSec }) {
  return axios.post('/api/task/nb_hld', {
    fileId, onnxName, targetDurationSec,
  })
}

export async function getHldTaskStatus(taskId) {
  const { data } = await axios.get(`/api/task/nb_hld/${taskId}`)
  return data
}

export function deleteHldTask(taskId) {
  return axios.delete(`/api/task/nb_hld/${taskId}`)
}

export function createTranscribeTask({ fileId, modelName }) {
  return axios.post('/api/task/transcribe', {
    fileId, modelName,
  })
}

export async function getTranscribeTaskStatus(taskId) {
  const { data } = await axios.get(`/api/task/transcribe/${taskId}`)
  return data
}

export function deleteTranscribeTask(taskId) {
  return axios.delete(`/api/task/transcribe/${taskId}`)
}

export function checkNWJsMediaExistence(fileIds) {
  return axios.post('/api/task/check_media_existence', { fileIds })
}

export function getNWJsMediaExistence(fileId) {
  return axios.get(`/api/task/check_media_existence?id=${fileId}`)
}
export function createExportProjectTask({ exportType, includeMedia, projectId, outputAbsPath }) {
  return axios.post('/api/task/export_project', { projectId, includeMedia, exportType, outputAbsPath })
}


export async function getExportProjectStatus(taskId) {
  const { data } = await axios.get(`/api/task/export_project?id=${taskId}`)
  return data
}

export function deleteExportProjectTask(taskId) {
  return axios.delete(`/api/task/export_project?id=${taskId}`)
}

export function deleteMediaFile(id, force = false, abortController) {
  let params = {}

  if (force) {
    params = { ...params, force }
  }

  return axios.delete(`/api/mediafiles/${id}`, {
    signal: abortController?.signal, crossDomain: true, params,
  }).then(handleError)
}

export function createWebSocketClient() {
  return new WebSocket(HOST_WS)
}

export function createUploadSession(name, size, folderId) {
  const params = { name, size }

  if (folderId) {
    params.folder = folderId
  }
  return axios.post('/api/upload/media', params)
}

export function uploadMediaFile({ id, chunk, start, end, totalSize }) {
  return axios.put(`/api/upload/media?id=${id}`, chunk, {
    headers: {
      'Content-type': 'application/octet-stream',
      'Content-Range': `bytes ${start}-${end}/${totalSize}`,
    },
  })
}

export function importFromUrl({ url, folder }) {
  return axios.post('/api/import/media/url', { url, folder })
}

export function importLiveStreamFromUrl({ url, folder }) {
  return axios.post('/api/live_streams', { url, folder })
}

export function captureLiveStream(id) {
  return axios.put(`/api/live_streams/${id}`)
}

export function getCaptureLiveStream(id, onData) {
  const controller = new AbortController()
  const request = async () => {
    try {
      const response = await axios.get(`/api/mediafiles/${id}`, { signal: controller.signal })
      onData(response.data)
      await new Promise(resolve => setTimeout(resolve, 500))
      await request()
    } catch (e) {
      if (e.message !== 'canceled') {
        await request()
      }
    }
  }
  request()
  return controller
}

export function deleteLiveStream(id, keepRecording = true) {
  return axios.delete(`/api/live_streams/${id}${keepRecording ? '?keepRecording=1' : ''}`)
}

export function switchLogEnabled(logsEnabled) {
  return axios.post('/api/backdoor/logswitch', { enable: logsEnabled })
}
export function restartApp() {
  return axios.post('/api/backdoor/restart', { })
}
export function installComponent({ component, lang = getLang() }) {
  return axios.post('/api/backdoor/install', { component, lang })
}

export function getProject(id) {
  const clientId = getClientId()
  return axios.get(`/api/projects/${id}?clientId=${clientId}`).then(res => res.data)
}

export function saveProject(id, project) {
  const httpsAgent = new https.Agent({ keepAlive: true })
  const body = {
    ...project,
    clientId: getClientId(),
  }
  return axios.patch(`/api/projects/${id}`, body, { httpsAgent })
}

export function renameProject(id, name) {
  const body = {
    name,
    clientId: getClientId(),
  }
  return axios.patch(`/api/projects/${id}`, body)
}

export function createProject(project) {
  const httpsAgent = new https.Agent({ keepAlive: true })
  const body = {
    ...project,
    clientId: getClientId(),
  }
  return axios.post('/api/projects', body, { httpsAgent }).then(res => res.data)
}

/**
 * Fetches a list of projects with filtering, pagination, and sorting.
 * * @param {string} search - Search query string (e.g., 'test(5)').
 * @param {Object} [options={}] - Additional request parameters.
 * @param {number} [options.start_number=0] - The offset/starting index for results.
 * @param {number} [options.count=20] - Maximum number of items to return.
 * @param {number} [options.data=0] - Data depth or flag identifier.
 * @param {string} [options.sort_field='createTime'] - The field used for ordering results. (by modiftime (default))
 * @param {string} [options.sort_order='desc'] - Sorting direction ('asc' or 'desc',  descending (default)).
 * @returns {Promise<{projects: Array, totalCount: number}>} Transformed response with entries and total count.
 */
export function getProjects(search, options = {}) {
  return axios.get('/api/projects', {
    params: { search, count: 20, data: 0, ...options },
  }).then(res => ({ projects: res.data.entries, totalCount: res.data.total_count }))
}

export function deleteProject(id) {
  return axios.delete(`/api/projects/${id}`)
}

export const saveHistoryAction = ({ projectId, historyAction }) => {
  const { id, createTime, name } = historyAction
  // console.log(`saveHistoryAction  ${id}`)
  const dataObject = getObjectFields(historyAction)
  delete dataObject.payload
  return axios.post('/api/actions', {
    id,
    project: projectId,
    name,
    data: JSON.stringify(dataObject),
    createTime,
  }).then(({ data: { id: actionId } }) => actionId)
}

export function checkPresenceInProjects(fileId, abortController) {
  return axios.get(`/api/projects/presence?file_id=${fileId}`, {
    signal: abortController?.signal,
  }).then(res => res.data)
}

export const undoHistoryAction = (actionId, modifTime) => axios.patch(`/api/actions/${actionId}`, { canceled: true, modifTime }).then(() => actionId)

export const redoHistoryAction = (actionId, modifTime) => axios.patch(`/api/actions/${actionId}`, { canceled: false, modifTime }).then(() => actionId)

export function importFromAmazon({ secretKey, bucketName, accessKeyId }) {
  return axios.post('/api/import/media/awsbucket',
    {
      awsAccessKey: accessKeyId,
      awsSecreteKey: secretKey,
      awsBucketName: bucketName,
    }).then(res => (res.data))
}

export function importFromLocalFile(path, size, folderId) {
  const params = { path, size }
  if (folderId) {
    params.folder = folderId
  }
  return axios.post('/api/import/media/local_file', params)
}

/**
 * @param {string[]} path
 * @param {number} size
 * @param {string|number} [folderId]
 * @returns {Promise<AxiosResponse<API.BatchResponse>>}
 */
export function importFromLocalFilesBatch(paths, sizes, folderId) {
  const params = { paths, sizes }
  if (folderId) {
    params.folder = folderId
  }
  return axios.post('/api/task/local_files_batch', params)
}

/**
 * @param {string} taskId
 * @returns {Promise<AxiosResponse<API.BatchRequestStatus>>}
 */
export function getLocalFilesBatchStatus(taskId) {
  return axios.get(`/api/task/local_files_batch?id=${taskId}`)
}

/**
 * @param {string} taskId
 * @returns {Promise<AxiosResponse<object>>}
 */
export function deleteLocalFilesBatchRequest(taskId) {
  return axios.delete(`/api/task/local_files_batch?id=${taskId}`)
}

export function createUser(body) {
  return axios.post('/api/users', body).then(r => r.data)
}

export function patchUser(id, body) {
  return axios.patch(`/api/users/${id}`, body,
    {
      headers: {
        Authorization: id,
      },
    })
}

export async function getTranslations(customLanguageUrl) {
  try {
    const data = await (await fetch(customLanguageUrl)).json()
    const lang = getLang()
    if (!data[lang]) {
      if (data.en) {
        return data.en
      }
      return translations.en
    }
    return data[lang]
  } catch {
    return translations.en
  }
}

export function patchDevProfile(id, body) {
  return axios.patch(`/api/dev_profiles/${id}`,
    body,
    {
      headers: {
        Authorization: id,
      },
    })
}

export function getStat() {
  return axios.get('/api/stat').then(res => res.data)
}

export function getUser(id) {
  return axios.get(`/api/users/${id}`).then(res => res.data)
}

export function getAdminUsers(params = {}) {
  return axios.get('/api/admin/users/', { params }).then(res => res.data)
}

export function getAdminProjects(params = {}) {
  return axios.get('/api/admin/projects', { params }).then(res => res.data)
}

export function getAdminMediaFiles(params = {}) {
  return axios.get('/api/admin/mediafiles', { params }).then(res => res.data)
}

export function getAdminMediaFileErrors(params = {}) {
  return axios.get('/api/admin/mediafile_errors', { params }).then(res => res.data)
}

export function getAdminRenderErrors(params = {}) {
  return axios.get('/api/admin/render_errors', { params }).then(res => res.data)
}

export async function updateReference(id, delta, controller = new AbortController()) {
  return axios.patch(`/api/mediafiles/${id}/reference?delta=${delta}`, { signal: controller.signal })
}

export async function updateProjectFileRefCount(projectId) {
  return axios.patch(`/api/mediafiles/references?projectId=${projectId}`, {})
}

export const httpClient = axios
